import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const StudentDashboardPage = () => {
  const { user, logout } = useContext(AuthContext);
  const [quizzes, setQuizzes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [courseLessons, setCourseLessons] = useState({}); // Store lessons by courseId
  const [isDashboardLoading, setIsDashboardLoading] = useState(true);
  const [stats, setStats] = useState({
    totalQuizzes: 0,
    completedQuizzes: 0,
    pendingQuizzes: 0,
    averageScore: 0
  });

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        if (isMounted) {
          setIsDashboardLoading(true);
        }

        const studentType = user?.studentType || '';

        // Fetch key dashboard resources in parallel for faster first paint.
        const [quizzesRes, coursesRes, submissionsRes] = await Promise.all([
          api.get('/quizzes?limit=100'),
          api.get('/courses?limit=100'),
          api.get('/submissions'),
        ]);

        const quizzesList = quizzesRes.data.data || quizzesRes.data || [];
        const coursesData = coursesRes.data.data || coursesRes.data || [];
        const userSubmissions = submissionsRes.data || [];

        if (!isMounted) return;

        // Render quizzes/tiles immediately.
        setQuizzes(quizzesList);
        setCourses(coursesData);
        setSubmissions(userSubmissions);

        // Calculate stats
        const totalQuizzes = quizzesList.length;
        const completedQuizzes = userSubmissions.length;
        const pendingQuizzes = totalQuizzes - completedQuizzes;
        const averageScore = userSubmissions.length > 0 
          ? userSubmissions.reduce((sum, sub) => sum + (sub.score || 0), 0) / userSubmissions.length
          : 0;

        setStats({
          totalQuizzes,
          completedQuizzes,
          pendingQuizzes,
          averageScore: Math.round(averageScore * 100) / 100
        });

        setIsDashboardLoading(false);

        // Fetch lesson status in background so it doesn't block dashboard tiles/quizzes.
        if (coursesData.length > 0) {
          const lessonResults = await Promise.allSettled(
            coursesData.map(course =>
              api.get(`/lessons/course/${course.id}?studentType=${studentType}`)
            )
          );

          if (!isMounted) return;

          const lessonsMap = {};
          coursesData.forEach((course, index) => {
            const result = lessonResults[index];
            if (result.status === 'fulfilled') {
              lessonsMap[course.id] = result.value.data || [];
            } else {
              console.error(`Error fetching lessons for course ${course.id}:`, result.reason);
              lessonsMap[course.id] = [];
            }
          });

          setCourseLessons(lessonsMap);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        if (isMounted) {
          setIsDashboardLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [user?.studentType]);

  const now = new Date();
  const openQuizzes = quizzes.filter(q => {
    const ot = q.openTime ? new Date(q.openTime) : null;
    const ct = q.closeTime ? new Date(q.closeTime) : null;
    return (!ot || now >= ot) && (!ct || now <= ct);
  });

  // Helper: check if quiz is submitted
  const isQuizSubmitted = quizId => submissions.some(s => s.quizId === quizId);

  // Helper: get course lesson status (NEW or UPDATED)
  const getCourseStatus = (courseId) => {
    const lessons = courseLessons[courseId] || [];
    if (lessons.length === 0) return null;

    const now = new Date();
    const threeDaysAgo = new Date(now.getTime() - (3 * 24 * 60 * 60 * 1000)); // 3 days
    const sevenDaysAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000)); // 7 days

    // Only consider lessons that haven't been viewed yet
    const unviewedLessons = lessons.filter(lesson => !lesson.viewedAt);

    // Check for new lessons (published in last 7 days and not viewed)
    const newLessons = unviewedLessons.filter(lesson => {
      const publishedDate = new Date(lesson.publishedAt);
      return lesson.publishedAt && publishedDate > sevenDaysAgo;
    });

    // Check for recently updated lessons (updated in last 3 days and not viewed)
    // Only show UPDATED if lesson is no longer "new"
    const updatedLessons = unviewedLessons.filter(lesson => {
      const updatedDate = new Date(lesson.updatedAt);
      const publishedDate = new Date(lesson.publishedAt);
      const createdDate = new Date(lesson.createdAt);
      
      // Only count as "updated" if:
      // 1. Published more than 7 days ago (no longer "new")
      // 2. Updated in last 3 days
      // 3. Updated significantly after creation (more than 1 day difference)
      return lesson.updatedAt && 
             lesson.publishedAt &&
             publishedDate <= sevenDaysAgo && // No longer "new"
             updatedDate > threeDaysAgo && 
             (updatedDate - createdDate) > (24 * 60 * 60 * 1000); // More than 1 day after creation
    });

    if (newLessons.length > 0) {
      return { type: 'NEW', count: newLessons.length, color: 'green' };
    } else if (updatedLessons.length > 0) {
      return { type: 'UPDATED', count: updatedLessons.length, color: 'blue' };
    }
    return null;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const completionRate = stats.totalQuizzes > 0
    ? Math.round((stats.completedQuizzes / stats.totalQuizzes) * 100)
    : 0;

  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

  const formatDueLabel = closeTime => {
    if (!closeTime) return 'No due date';
    const due = new Date(closeTime);
    const isToday = due.toDateString() === new Date().toDateString();
    return isToday
      ? `Due today • ${due.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
      : `Due ${due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 4 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.22, ease: 'easeOut' },
    },
  };

  const gridVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.015, delayChildren: 0 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 3 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.18, ease: 'easeOut' },
    },
  };

  const revealOnScroll = { once: false, amount: 0.3 };

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        variants={sectionVariants}
        initial={false}
        whileInView="show"
        viewport={revealOnScroll}
        className="rounded-3xl border border-blue-100 bg-gradient-to-r from-white via-blue-50 to-indigo-50 p-6 shadow-sm"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-700">Student Dashboard</p>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">
                {getGreeting()}, {user.name}! 👋
              </h1>
              <p className="mt-1 text-slate-600">Ready to learn something meaningful today?</p>
              <div className="mt-4 inline-flex items-center rounded-full border border-blue-200 bg-white px-3 py-1 text-xs font-medium text-blue-700">
                {todayLabel}
              </div>
            </div>
            <div className="flex flex-col items-start gap-3 sm:items-end">
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-2">
                <p className="text-xs uppercase tracking-wide text-slate-500">Learning Momentum</p>
                <p className="text-sm font-semibold text-slate-800">
                  {isDashboardLoading ? 'Loading progress...' : `${completionRate}% quizzes completed`}
                </p>
              </div>
              <button 
                onClick={logout} 
                className="inline-flex items-center justify-center rounded-xl border border-red-200 bg-red-50 px-5 py-2.5 text-sm font-semibold text-red-700 transition hover:bg-red-100"
              >
                Logout
              </button>
            </div>
        </div>
      </motion.div>

        {/* Stats Cards */}
        <motion.div
          variants={gridVariants}
          initial={false}
          whileInView="show"
          viewport={revealOnScroll}
          className="grid grid-cols-2 gap-4 md:grid-cols-2 lg:grid-cols-4 lg:gap-5"
        >
          <motion.div variants={cardVariants} className="aspect-square rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm md:aspect-auto md:p-6">
            <div className="flex h-full flex-col items-start justify-between md:h-auto md:flex-row md:items-center md:justify-start">
              <div className="rounded-xl bg-blue-100 p-3">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="md:ml-4">
                <p className="text-xs font-medium text-slate-500 md:text-sm">Total Quizzes</p>
                {isDashboardLoading ? (
                  <div className="mt-1 h-7 w-14 animate-pulse rounded bg-slate-200" />
                ) : (
                  <p className="text-2xl font-bold text-slate-900">{stats.totalQuizzes}</p>
                )}
              </div>
            </div>
          </motion.div>

          <motion.div variants={cardVariants} className="aspect-square rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm md:aspect-auto md:p-6">
            <div className="flex h-full flex-col items-start justify-between md:h-auto md:flex-row md:items-center md:justify-start">
              <div className="rounded-xl bg-green-100 p-3">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="md:ml-4">
                <p className="text-xs font-medium text-slate-500 md:text-sm">Completed</p>
                {isDashboardLoading ? (
                  <div className="mt-1 h-7 w-14 animate-pulse rounded bg-slate-200" />
                ) : (
                  <p className="text-2xl font-bold text-slate-900">{stats.completedQuizzes}</p>
                )}
              </div>
            </div>
          </motion.div>

          <motion.div variants={cardVariants} className="aspect-square rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm md:aspect-auto md:p-6">
            <div className="flex h-full flex-col items-start justify-between md:h-auto md:flex-row md:items-center md:justify-start">
              <div className="rounded-xl bg-amber-100 p-3">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="md:ml-4">
                <p className="text-xs font-medium text-slate-500 md:text-sm">Pending</p>
                {isDashboardLoading ? (
                  <div className="mt-1 h-7 w-14 animate-pulse rounded bg-slate-200" />
                ) : (
                  <p className="text-2xl font-bold text-slate-900">{stats.pendingQuizzes}</p>
                )}
              </div>
            </div>
          </motion.div>

          <motion.div variants={cardVariants} className="aspect-square rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm md:aspect-auto md:p-6">
            <div className="flex h-full flex-col items-start justify-between md:h-auto md:flex-row md:items-center md:justify-start">
              <div className="rounded-xl bg-purple-100 p-3">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="md:ml-4">
                <p className="text-xs font-medium text-slate-500 md:text-sm">Average Score</p>
                {isDashboardLoading ? (
                  <div className="mt-1 h-7 w-16 animate-pulse rounded bg-slate-200" />
                ) : (
                  <p className="text-2xl font-bold text-slate-900">{stats.averageScore}%</p>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Available Quizzes */}
          <div className="lg:col-span-2">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 bg-gradient-to-r from-white to-slate-50 p-4 sm:p-6">
                <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <div>
                    <h2 className="flex items-center text-lg font-bold text-slate-900 sm:text-xl">
                      <svg className="mr-2 h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Available Quizzes
                    </h2>
                    <p className="mt-1 text-sm text-slate-500">Keep your momentum—complete what&apos;s open first.</p>
                  </div>
                  <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                    {isDashboardLoading ? 'Loading...' : `${openQuizzes.length} open now`}
                  </span>
                </div>
              </div>
              <div className="p-4 sm:p-6">
                {isDashboardLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(item => (
                      <div key={item} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="h-4 w-1/2 animate-pulse rounded bg-slate-200" />
                        <div className="mt-2 h-3 w-1/3 animate-pulse rounded bg-slate-200" />
                      </div>
                    ))}
                  </div>
                ) : openQuizzes.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/80 py-12 text-center">
                    <svg className="mx-auto mb-4 h-16 w-16 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-lg font-semibold text-slate-700">No quizzes available right now</p>
                    <p className="mt-1 text-sm text-slate-500">Check back later for new assignments.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {openQuizzes.slice(0, 5).map(q => (
                      <div key={q.id} className="group flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-sm sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start space-x-3 sm:items-center sm:space-x-4">
                          <div className="rounded-lg bg-blue-100 p-2.5 ring-1 ring-blue-200/60">
                            <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div className="min-w-0 space-y-1">
                            <h3 className="font-semibold text-slate-900 group-hover:text-blue-700">{q.title}</h3>
                            <p className="text-sm text-slate-500">{formatDueLabel(q.closeTime)}</p>
                          </div>
                        </div>
                        <div className="w-full sm:ml-4 sm:w-auto">
                          {isQuizSubmitted(q.id) ? (
                            <span className="inline-flex w-full items-center justify-center rounded-full border border-green-200 bg-green-50 px-3 py-1 text-sm font-semibold text-green-700 sm:w-auto">
                              <svg className="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Submitted
                            </span>
                          ) : (
                            <Link 
                              to={`/students/dashboard/quizzes/${q.id}/attempt`} 
                              className="inline-flex w-full items-center justify-center rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-800 sm:w-auto"
                            >
                              Start Quiz
                              <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                    {openQuizzes.length > 5 && (
                      <div className="text-center pt-4">
                        <Link to="/students/dashboard/quizzes" className="font-medium text-blue-700 hover:text-blue-800">
                          View all {openQuizzes.length} quizzes →
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 p-6">
                <h3 className="flex items-center text-lg font-bold text-slate-900">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Quick Actions
                </h3>
              </div>
              <div className="p-6 space-y-3">
                <Link 
                  to="/students/dashboard/courses" 
                  className="flex items-center rounded-xl border border-slate-200 p-3 transition hover:bg-slate-50"
                >
                  <svg className="w-5 h-5 text-indigo-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.168 18.477 18.582 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <div>
                    <p className="font-medium text-slate-900">My Courses</p>
                    <p className="text-xs text-slate-500">Continue your lessons</p>
                  </div>
                </Link>
                
                <Link 
                  to="/students/dashboard/records" 
                  className="flex items-center rounded-xl border border-slate-200 p-3 transition hover:bg-slate-50"
                >
                  <svg className="w-5 h-5 text-orange-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <div>
                    <p className="font-medium text-slate-900">View Records</p>
                    <p className="text-xs text-slate-500">Track your performance history</p>
                  </div>
                </Link>
              </div>
            </div>

            {/* Course Progress */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 p-6">
                <h3 className="flex items-center text-lg font-bold text-slate-900">
                  <svg className="w-5 h-5 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.168 18.477 18.582 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  My Courses ({courses.length})
                </h3>
              </div>
              <div className="p-6">
                {courses.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No courses enrolled</p>
                ) : (
                  <div className="space-y-3">
                    {courses.slice(0, 4).map(course => {
                      const status = getCourseStatus(course.id);
                      return (
                        <Link 
                          key={course.id}
                          to={`/students/dashboard/courses/${course.id}/view`}
                          className="block rounded-xl border border-slate-200 p-3 transition hover:bg-slate-50"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-slate-900">{course.title || course.name}</h4>
                              <p className="mt-1 text-xs text-slate-500">Click to view details</p>
                            </div>
                            {status && (
                              <span className={`ml-3 px-2 py-1 rounded-full text-xs font-semibold ${
                                status.color === 'green' 
                                  ? 'bg-green-100 text-green-700 border border-green-300' 
                                  : 'bg-blue-100 text-blue-700 border border-blue-300'
                              }`}>
                                {status.type} {status.count > 1 ? `(${status.count})` : ''}
                              </span>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                    {courses.length > 4 && (
                      <div className="text-center pt-2">
                        <Link to="/students/dashboard/courses" className="text-sm font-medium text-blue-700 hover:text-blue-800">
                          View all courses →
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
    </div>
  );
};

export default StudentDashboardPage;
